import {computed, makeObservable, override} from "mobx";
import {BaseModuleState} from "../../../infrastructure/models/base-module-state";
import {BaseMobxState} from "../../../infrastructure/mobx/base-mobx-state";
import {NavigationManager} from "../../../core/managers/navigation-manager";
import {AppStore} from "../../../core/app/app-store";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {DeviceViewModel} from "../../../core/models/device.view-model";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {NewsWidgetConfig} from "../news-widget.config";
import {NewsWidgetItemViewModel} from "./news-widget.item.view-model";
import {NewsWidgetProps} from "./news-widget.props";
import {PAGES, ROUTE_NAMES} from "../../../core/consts";
import {NewsDomainModel} from "../../../domain/news/models/news.domain-model";
import {BUZZ_URL} from "../../../apps/website/modules/main-header/main-header-module.consts";
import {BaseUrlModel} from "../../../infrastructure/models/link/base-url-model";
import {BaseLinkModelSettings} from "../../../infrastructure/models/link/base-link-model-settings";
import {WebsiteModuleState} from "../../../apps/website/models/website-module.state";
import {EntityDescriptionModuleConfig} from "../../../modules/entity-description-module/entity-description.module.config";
import {CompetitionNewsPageLinkModel} from "../../../containers/competition-container/pages/news-page/models/competition-news-page.link-model";
import {CompetitorNewsPageLinkModel} from "../../../containers/competitor-container/pages/news-page/models/competitor.news-page.link-model";

export class NewsWidgetState extends BaseModuleState {
  props: NewsWidgetProps;

  static injectables = [...BaseMobxState.injectables, NavigationManager, AppStore, LoggerUtils, DatetimeUtils, DeviceViewModel, TermsUtils, NewsWidgetConfig, WebsiteModuleState];
  constructor(
      navigationManager: NavigationManager,
      appStore: AppStore,
      logger: LoggerUtils,
      datetimeUtils: DatetimeUtils,
      device: DeviceViewModel,
      termsUtils: TermsUtils,
      config: EntityDescriptionModuleConfig,
      websiteModuleState: WebsiteModuleState,
  ) {
    super(navigationManager, appStore, logger, datetimeUtils, device, termsUtils, config);

    this.websiteModuleState = websiteModuleState;

    makeObservable(this)
  }

  @override get isPopulated() {
    return !this.appState.isEurobetLive && (this.isPreview ? this.news?.length > 0 : true);
  }

  get isPreview(): Boolean {
    return this.props.isPreview;
  }

  @computed get news(): Array<NewsWidgetItemViewModel> {
    const news: Array<NewsWidgetItemViewModel> = this.dataSource?.news?.sortDescending('publishDate').map((newsItem: NewsDomainModel, index: Number) => {
      const publishTime = this.calcPublishTimeText(newsItem.publishDate);
      return new NewsWidgetItemViewModel(newsItem, publishTime, this.isPreview, this.device.isSmallView, index);
    });

    return news;
  }

  @computed get isCompetitionContext(): Boolean {
    return this.props.competitionsIds?.length > 0;
  }

  @computed get isCompetitorContext(): Boolean {
    return this.props.competitorsIds?.length > 0;
  }

  @computed get isGameContext(): Boolean {
    return this.props.gamesIds?.length > 0;
  }

  @computed get isAthleteContext(): Boolean {
    return this.currentLinkModel && this.currentLinkModel.name === ROUTE_NAMES.athlete;
  }

  @computed get isAllScores(): Boolean {
    return this.currentLinkModel && this.currentLinkModel.name === ROUTE_NAMES.allScores;
  }

  @computed get shouldRenderSeeAll(): Boolean {
    let shouldRenderSeeAll: Boolean = this.news?.length > 0 && this.isPreview;
    if(shouldRenderSeeAll) {
      if(this.isAllScores && !this.isGameContext){
        shouldRenderSeeAll = this.appState.shouldRenderBuzzItem;
      }
      else if(this.isAthleteContext){
        shouldRenderSeeAll = false;
      }
    }

    return this.props.shouldRenderSeeAll && shouldRenderSeeAll;
  }

  @computed get competitionSeeAllLinkModel(): CompetitionNewsPageLinkModel {
    const competition = this.websiteModuleState?.entitiesContext.competition;
    return competition ? new CompetitionNewsPageLinkModel(competition.id, competition.sport.nameForURL, competition.nameForURL, competition.isFollow) : null;
  }

  @computed get competitorSeeAllLinkModel(): CompetitorNewsPageLinkModel {
    const competitor = this.websiteModuleState?.entitiesContext.competitor;
    return competitor ? new CompetitorNewsPageLinkModel(competitor.id, competitor.sport.nameForURL, competitor.nameForURL, competitor.isFollow) : null;
  }

  @computed get seeAllLink(): String {
    let seeAllLink = null;
    const setting =  new BaseLinkModelSettings();
    setting.shouldOpenNewTab = true;
    if(this.isAllScores && !this.isGameContext){
      seeAllLink = new BaseUrlModel(BUZZ_URL[this.language?.displayName]?.url, true, setting);
    }
    else if(this.isCompetitionContext) {
      seeAllLink = this.competitionSeeAllLinkModel;
    } else if(this.isCompetitorContext) {
      seeAllLink = this.competitorSeeAllLinkModel;
    }

    return seeAllLink;
  }

  calcPublishTimeText(publishDate: Date) {
    let publishTimeText = '';
    if ((this.datetimeUtils.diffMinutesByDate(publishDate) * (-1)) < 60) {
      const diffMinutes = Math.round(this.datetimeUtils.diffMinutesByDate(publishDate) * (-1));
      publishTimeText = this.terms.WEB_ARTICLE_MINUTES_AGO.replace('#', diffMinutes);
    }
    else if ((this.datetimeUtils.diffHoursByDate(publishDate) * (-1)) < 24) {
      const diffHours = Math.round(this.datetimeUtils.diffHoursByDate(publishDate) * (-1));
      publishTimeText =  this.terms.WEB_ARTICLE_HOURS_AGO.replace('#', diffHours);
    }
    else if ((this.datetimeUtils.diffHoursByDate(publishDate) * (-1)) < 48 && this.datetimeUtils.isYesterday(publishDate)) {
      publishTimeText =  this.terms.WEB_ARTICLE_YESTERDAY;
    }
    else {
      const diffDays = Math.round(this.datetimeUtils.diffDaysByDate(publishDate) * (-1));
      publishTimeText =  this.terms.WEB_ARTICLE_DAYS_AGO.replace('#', diffDays);
    }
    return publishTimeText;
  }

  get magazineId(): String {
    const magazineId =  BUZZ_URL[this.language?.displayName]?.id;

    return magazineId;
  }

  get title(): String {
    const entityContext = this.websiteModuleState?.entitiesContext;
    let gameName = undefined;

    if(entityContext?.game) {
      gameName = this.terms.WEB_MATCHUP_INPAGE_TITLE_TEMPLATE?.format({
        "HOME_COMPETITOR": entityContext?.game.homeCompetitor.name,
        "GAME_TITLE_VS": this.terms.GAME_TITLE_VS,
        "AWAY_COMPETITOR": entityContext?.game.awayCompetitor.name,
      });
    }

    return this.terms.NEWS_FEED_CARD_HEADER_TITLE.format({
      ENTITY_NAME: gameName || entityContext?.athlete?.name || entityContext?.competitor?.name || entityContext?.competition?.name || this.appState.context.sportType?.name || '',
    });
  }

  @computed get shouldRenderEmptyState(): Boolean {
    return !this.props.isPreview && this.news?.length === 0;
  }

  get emptyStateText(): String {
    return this.terms.WEB_NEWS_EMPTY_STATE_TEXT;
  }

  get shouldRenderMicroData(): String {
    return true;
  }

  @computed get microDataName(): String {
    return this.title;
  }

  @computed get isBettingPage(): Boolean {
    const bettingPages: Array<ROUTE_NAMES> = [ROUTE_NAMES.betting, ...(Object.values(PAGES.BETTING))];

    return bettingPages.includes(this.currentLinkModel?.name);
  }
}
